import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import useSWR from 'swr';

import { fetchMcpConnectionSettings } from '../../features/mcpConnections';

export function ChatGptSetup({ userId }: { userId: string }) {
  const { data, error, isLoading, mutate } = useSWR(
    ['mcp-connection-settings', userId],
    fetchMcpConnectionSettings,
    { shouldRetryOnError: false },
  );
  const [copyStatus, setCopyStatus] = useState<string | null>(null);

  async function copyUrl() {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.mcpUrl);
      setCopyStatus('MCP URL copied.');
    } catch {
      setCopyStatus('Could not copy automatically. Select and copy the MCP URL above.');
    }
  }

  return (
    <Stack spacing={1.5}>
      <Typography component="h2" variant="h6" fontWeight={800}>
        Connect ChatGPT
      </Typography>
      {isLoading ? <CircularProgress size={24} aria-label="Loading ChatGPT setup" /> : null}
      {error ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" onClick={() => void mutate()}>
              Retry setup
            </Button>
          }
        >
          {axios.isAxiosError(error) && error.response?.status === 404
            ? 'ChatGPT setup is not available for this Paddiq service.'
            : 'Could not load ChatGPT setup. Retry to check availability.'}
        </Alert>
      ) : null}
      {data && !error ? (
        <>
          {data.publicPluginUrl ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Install the public Paddiq plugin in ChatGPT. Existing connections can keep their
                current access.
              </Typography>
              <Button
                component="a"
                href={data.publicPluginUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
              >
                Get Paddiq in ChatGPT
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              The public Paddiq plugin is awaiting review. Use this temporary custom connection if
              your ChatGPT account allows it.
            </Typography>
          )}
          {!data.publicPluginUrl && data.selfServiceAvailable ? (
            <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
              >
                <TextField
                  label="MCP server URL"
                  value={data.mcpUrl}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText="For this Paddiq service. Copy the complete URL, including /mcp."
                />
                <Button
                  variant="outlined"
                  onClick={() => void copyUrl()}
                  sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                >
                  Copy URL
                </Button>
              </Stack>
              {copyStatus ? (
                <Typography role="status" variant="body2">
                  {copyStatus}
                </Typography>
              ) : null}
              <Box component="ol" sx={{ m: 0, pl: 2.5, '& li + li': { mt: 1 } }}>
                <Typography component="li" variant="body2">
                  Open{' '}
                  <Link
                    href="https://chatgpt.com/plugins"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ChatGPT Plugins
                  </Link>
                  , select <strong>+ → Add custom MCP server</strong>, and name it Paddiq. Add a
                  description, such as “Connect to my Paddiq sessions and telemetry.” Under{' '}
                  <strong>Connection</strong>, enter the complete MCP URL above as the public
                  endpoint.
                </Typography>
                <Typography component="li" variant="body2">
                  Choose OAuth and client metadata (CIMD) if ChatGPT offers this supported path.
                  With that supported Paddiq CIMD path, no client secret or manual callback
                  registration is needed. Read the warning, select{' '}
                  <strong>I understand and want to continue</strong>, then{' '}
                  <strong>Create as a plugin</strong>.
                </Typography>
                <Typography component="li" variant="body2">
                  Review the discovered tools and plugin details. If discovery fails, resolve the
                  error before continuing. Install the plugin, then start a new chat and select
                  Paddiq with <strong>@</strong>.
                </Typography>
                <Typography component="li" variant="body2">
                  When prompted, sign in to your own Paddiq account. Check the account, team and
                  requested permissions, and approve only the access you want.
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Custom MCP access depends on your ChatGPT account, plan and workspace policy. If the
                option is missing, check your account’s eligibility and, for a managed workspace,
                ask your administrator about policy. Administrator approval alone does not guarantee
                availability. Paddiq permissions still apply; this connection cannot expand your
                role.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If CIMD is not offered, or ChatGPT asks for a secret or a callback to register, stop
                and contact Paddiq support.
              </Typography>
              <Alert severity="info">
                These steps follow OpenAI’s documentation and Paddiq’s discovery checks. A complete
                customer sign-in through this CIMD setup has not yet been verified.
              </Alert>
            </>
          ) : !data.publicPluginUrl ? (
            <Alert severity="info">
              Self-service ChatGPT setup is not available for this Paddiq service. Contact Paddiq
              support; you can still manage existing access below.
            </Alert>
          ) : null}
          <Link
            href="https://developers.openai.com/plugins/deploy/connect-chatgpt"
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
          >
            ChatGPT connection instructions
          </Link>
        </>
      ) : null}
    </Stack>
  );
}
